/**
 * Industry-specific role tasks configuration
 * Extracted from UnifiedDashboard for maintainability
 */

// Industry-specific role tasks
export const INDUSTRY_ROLE_TASKS = {
  // Neutral fallback for any industry without a bespoke set — never hospitality.
  general: {
    owner: [
      { title: 'Review weekly profit & loss', priority: 'high', category: 'financial' },
      { title: 'Review pricing and margins', priority: 'high', category: 'operations' },
      { title: 'Team performance check-ins', priority: 'medium', category: 'hr' },
      { title: 'Review customer feedback trends', priority: 'medium', category: 'service' },
      { title: 'Supplier contract review', priority: 'low', category: 'procurement' }
    ],
    'general-manager': [
      { title: 'Daily revenue reconciliation', priority: 'high', category: 'financial' },
      { title: 'Roster approval for next week', priority: 'high', category: 'hr' },
      { title: 'Follow up overdue invoices', priority: 'high', category: 'financial' },
      { title: 'Customer follow-up review', priority: 'medium', category: 'service' }
    ]
  },
  hospitality: {
    owner: [
      { title: 'Review weekly P&L report', priority: 'high', category: 'financial' },
      { title: 'Approve new menu pricing', priority: 'high', category: 'operations' },
      { title: 'Staff performance reviews', priority: 'medium', category: 'hr' },
      { title: 'Review customer feedback trends', priority: 'medium', category: 'service' },
      { title: 'Supplier contract renewal', priority: 'low', category: 'procurement' }
    ],
    'general-manager': [
      { title: 'Daily sales reconciliation', priority: 'high', category: 'financial' },
      { title: 'Roster approval for next week', priority: 'high', category: 'hr' },
      { title: 'Supplier meeting - Beverage', priority: 'medium', category: 'operations' },
      { title: 'Customer complaint follow-up', priority: 'high', category: 'service' }
    ],
    'kitchen-manager': [
      { title: 'Complete stock take', priority: 'high', category: 'inventory' },
      { title: 'Update menu costings', priority: 'medium', category: 'financial' },
      { title: 'Train new kitchen hand', priority: 'medium', category: 'hr' },
      { title: 'Health inspection prep', priority: 'high', category: 'compliance' }
    ]
  },
  retail: {
    owner: [
      { title: 'Review sales performance by category', priority: 'high', category: 'financial' },
      { title: 'Approve seasonal buying plan', priority: 'high', category: 'procurement' },
      { title: 'Review shrinkage report', priority: 'medium', category: 'loss-prevention' },
      { title: 'Staff incentive program review', priority: 'medium', category: 'hr' }
    ],
    'general-manager': [
      { title: 'Daily sales briefing', priority: 'high', category: 'operations' },
      { title: 'Visual merchandising update', priority: 'medium', category: 'marketing' },
      { title: 'Stock replenishment order', priority: 'high', category: 'inventory' },
      { title: 'Mystery shopper results review', priority: 'medium', category: 'service' }
    ]
  },
  healthcare: {
    owner: [
      { title: 'Review monthly billings', priority: 'high', category: 'financial' },
      { title: 'Practitioner schedule optimization', priority: 'high', category: 'operations' },
      { title: 'Equipment maintenance schedule', priority: 'medium', category: 'compliance' },
      { title: 'Patient satisfaction review', priority: 'medium', category: 'service' }
    ],
    'general-manager': [
      { title: 'Daily appointment review', priority: 'high', category: 'operations' },
      { title: 'Insurance claim follow-ups', priority: 'high', category: 'financial' },
      { title: 'Staff certification tracking', priority: 'medium', category: 'compliance' },
      { title: 'Patient feedback response', priority: 'high', category: 'service' }
    ]
  },
  fitness: {
    owner: [
      { title: 'Membership retention analysis', priority: 'high', category: 'financial' },
      { title: 'Equipment replacement budget', priority: 'medium', category: 'operations' },
      { title: 'Trainer performance reviews', priority: 'medium', category: 'hr' },
      { title: 'Class schedule optimization', priority: 'high', category: 'operations' }
    ],
    'general-manager': [
      { title: 'Daily check-in report', priority: 'high', category: 'operations' },
      { title: 'PT session scheduling', priority: 'high', category: 'service' },
      { title: 'Equipment maintenance check', priority: 'medium', category: 'facilities' },
      { title: 'New member onboarding', priority: 'high', category: 'service' }
    ]
  },
  technology: {
    owner: [
      { title: 'Review MRR trends', priority: 'high', category: 'financial' },
      { title: 'Product roadmap review', priority: 'high', category: 'product' },
      { title: 'Engineering capacity planning', priority: 'medium', category: 'hr' },
      { title: 'Customer churn analysis', priority: 'high', category: 'service' }
    ],
    'general-manager': [
      { title: 'Sprint progress review', priority: 'high', category: 'operations' },
      { title: 'Customer support metrics', priority: 'high', category: 'service' },
      { title: 'Infrastructure cost review', priority: 'medium', category: 'financial' },
      { title: 'Team standup facilitation', priority: 'medium', category: 'hr' }
    ]
  },
  professional: {
    owner: [
      { title: 'Review billable hours utilization', priority: 'high', category: 'financial' },
      { title: 'Client portfolio review', priority: 'high', category: 'client' },
      { title: 'Partner meeting preparation', priority: 'medium', category: 'operations' },
      { title: 'Business development pipeline', priority: 'medium', category: 'sales' }
    ],
    'general-manager': [
      { title: 'Matter assignment review', priority: 'high', category: 'operations' },
      { title: 'WIP aging report', priority: 'high', category: 'financial' },
      { title: 'Staff utilization analysis', priority: 'medium', category: 'hr' },
      { title: 'Client meeting schedule', priority: 'medium', category: 'service' }
    ]
  }
};

import { INDUSTRY_TEAM_MEMBERS, INDUSTRY_RECOMMENDATIONS, getIndustryData } from '@/config/industryDemoData';

// Industries that share the neutral "professional services" task/activity set.
const PROFESSIONAL_ALIASES = ['legal', 'accounting', 'finance', 'property', 'consulting', 'professional services', 'admin_services', 'media', 'events'];
const resolveTaskKey = (collection, industry) => {
  const k = (industry || '').toLowerCase().trim();
  if (collection[k]) return k;
  if (PROFESSIONAL_ALIASES.includes(k) && collection.professional) return 'professional';
  return 'general';
};

// Generate demo tasks based on role and industry
export const generateDemoTasks = (role, industry = 'general') => {
  const industryTasks = INDUSTRY_ROLE_TASKS[resolveTaskKey(INDUSTRY_ROLE_TASKS, industry)] || INDUSTRY_ROLE_TASKS.general;
  const roleTasks = industryTasks[role] || industryTasks.owner || [];
  
  return roleTasks.map((task, idx) => ({
    id: `task-${idx}`,
    ...task,
    completed: idx > 2,
    dueDate: idx === 0 ? 'Today' : idx === 1 ? 'Tomorrow' : `In ${idx + 1} days`
  }));
};

// Generate AI recommendations based on role and industry
export const generateRecommendations = (role, industry = 'general') => {
  return getIndustryData(INDUSTRY_RECOMMENDATIONS, industry);
};

// Generate team members for display (industry-aware)
export const generateTeamMembers = (industry = 'general') => {
  return getIndustryData(INDUSTRY_TEAM_MEMBERS, industry);
};

// Activity types for recent activity
export const ACTIVITY_TYPES = [
  { type: 'task', icon: 'CheckCircle2', color: 'emerald', action: 'completed' },
  { type: 'kpi', icon: 'TrendingUp', color: 'blue', action: 'improved' },
  { type: 'alert', icon: 'AlertTriangle', color: 'amber', action: 'flagged' },
  { type: 'team', icon: 'Users', color: 'purple', action: 'joined' },
  { type: 'insight', icon: 'Sparkles', color: 'primary', action: 'generated' }
];

// Activity messages by industry
export const ACTIVITY_MESSAGES = {
  // Neutral fallback — never hospitality.
  general: [
    { msg: 'Revenue tracking 7% ahead of last week', type: 'kpi', time: '2m ago' },
    { msg: 'Overdue invoice reminders sent to 3 accounts', type: 'alert', time: '6m ago' },
    { msg: 'Weekly reconciliation completed', type: 'task', time: '15m ago' },
    { msg: 'AI: Follow up new enquiries within the hour to lift conversion', type: 'insight', time: '20m ago' },
    { msg: 'Team roster updated for next week', type: 'team', time: '28m ago' }
  ],
  hospitality: [
    { msg: 'Table turnover rate improved by 12%', type: 'kpi', time: '2m ago' },
    { msg: 'Evening reservations fully booked', type: 'alert', time: '5m ago' },
    { msg: 'Kitchen prep completed for dinner service', type: 'task', time: '15m ago' },
    { msg: 'AI: Consider adding dessert special tonight', type: 'insight', time: '20m ago' },
    { msg: 'Staff break schedule updated', type: 'team', time: '25m ago' }
  ],
  retail: [
    { msg: 'Conversion rate up 8% this hour', type: 'kpi', time: '3m ago' },
    { msg: 'Low stock alert: Popular item #234', type: 'alert', time: '8m ago' },
    { msg: 'End-of-day till reconciliation complete', type: 'task', time: '12m ago' },
    { msg: 'AI: Peak traffic expected at 2pm', type: 'insight', time: '18m ago' },
    { msg: 'New staff member trained on POS', type: 'team', time: '30m ago' }
  ],
  healthcare: [
    { msg: 'Patient satisfaction score: 4.8/5', type: 'kpi', time: '5m ago' },
    { msg: 'Dr. Smith has 2 open slots today', type: 'alert', time: '10m ago' },
    { msg: 'Insurance claims processed: 12', type: 'task', time: '18m ago' },
    { msg: 'AI: Consider waitlist outreach', type: 'insight', time: '22m ago' },
    { msg: 'New patient onboarding completed', type: 'team', time: '35m ago' }
  ],
  professional: [
    { msg: 'Billable hours target: 92% achieved', type: 'kpi', time: '4m ago' },
    { msg: 'Client proposal due tomorrow', type: 'alert', time: '12m ago' },
    { msg: 'Quarterly report draft submitted', type: 'task', time: '20m ago' },
    { msg: 'AI: Client upsell opportunity detected', type: 'insight', time: '28m ago' },
    { msg: 'New consultant assigned to project', type: 'team', time: '40m ago' }
  ],
  technology: [
    { msg: 'MRR increased by 5% this month', type: 'kpi', time: '3m ago' },
    { msg: 'Critical bug reported in production', type: 'alert', time: '7m ago' },
    { msg: 'Sprint planning completed', type: 'task', time: '15m ago' },
    { msg: 'AI: Customer churn risk detected', type: 'insight', time: '25m ago' },
    { msg: 'New engineer onboarded', type: 'team', time: '45m ago' }
  ],
  fitness: [
    { msg: 'Membership renewals up 15%', type: 'kpi', time: '4m ago' },
    { msg: '3 PT sessions available today', type: 'alert', time: '9m ago' },
    { msg: 'Equipment maintenance completed', type: 'task', time: '16m ago' },
    { msg: 'AI: Peak hours predicted 6-8pm', type: 'insight', time: '22m ago' },
    { msg: 'New trainer certified', type: 'team', time: '38m ago' }
  ]
};

// Generate recent activity for an industry
export const generateRecentActivity = (industry) => {
  const activities = ACTIVITY_MESSAGES[resolveTaskKey(ACTIVITY_MESSAGES, industry)] || ACTIVITY_MESSAGES.general;
  return activities.map((a, idx) => ({
    ...a,
    id: idx,
    ...ACTIVITY_TYPES.find(t => t.type === a.type)
  }));
};

// Owner/manager roles that can see team view
export const OWNER_MANAGER_ROLES = [
  'owner', 'venue-operator', 'general-manager', 'advisor', 'investor',
  // Industry-specific owner roles
  'store-owner', 'practice-owner', 'managing-partner', 'gym-owner', 'agency-principal',
  'company-director', 'dealer-principal', 'school-director', 'salon-owner', 'factory-owner',
  'founder-ceo', 'firm-principal', 'hotel-owner', 'business-owner', 'senior-partner',
  'venue-owner', 'farm-owner', 'director',
  // QSR & franchise owner/manager roles
  'cleaning-business-owner', 'pet-business-owner', 'center-director', 'care-provider-owner',
  'pharmacy-owner', 'agency-owner', 'print-shop-owner', 'pest-control-owner', 'moving-company-owner',
  'company-owner',
  // Industry-specific manager roles
  'store-manager', 'practice-manager', 'operations-director', 'club-manager', 'sales-director',
  'project-director', 'dealership-manager', 'campus-manager', 'salon-manager', 'plant-manager',
  'coo', 'managing-director', 'hotel-manager', 'operations-manager', 'gaming-manager',
  'area-manager', 'facility-manager', 'center-manager', 'care-manager', 'pharmacy-manager',
  'branch-manager', 'production-manager', 'firm-owner',
];
